import { lazy, useRef, useState, FC, Suspense, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { renderSnackbar } from 'utils';
import { deferImport } from 'utils/helper';
import { IconButton, Badge, Tooltip, Avatar } from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import LoadingModal from 'components/commons/loading-modal';

/* Lazy load component */
const CropImagePopup = lazy((): Promise<any> => {
  return deferImport(import('components/popups/crop-image-popup'));
});
/* End */

interface UploadImageProps {
  onUpload: any;
  size?: number;
  imageUrl: string;
  component: FC<any>;
  checkSize?: boolean;
  disabled?: boolean;
  tooltip?: string;
}
const MAX_SIZE_IMAGE = 2097152; // 2 MB

const UploadImage = ({
  onUpload,
  size = 70,
  imageUrl,
  component,
  checkSize = true,
  disabled = false,
  tooltip = '',
}: UploadImageProps) => {
  const { t } = useTranslation(['validator']);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [defaultImage, setDefaultImage] = useState<string | null>(null);
  const IconComponent = component;

  return (
    <>
      <Tooltip title={tooltip}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <IconButton
              style={{
                color: '#707070',
                backgroundColor: '#fff',
                borderRadius: 50,
                padding: 3,
              }}
              disabled={disabled}
            >
              <FileUpload />
            </IconButton>
          }
          onClick={() => imageInputRef.current?.click()}
        >
          {imageUrl ? (
            <Avatar
              style={{
                height: size,
                width: size,
                border: '1px solid gray',
              }}
              imgProps={{ sx: { objectFit: 'contain' } }}
              src={imageUrl}
            />
          ) : (
            <IconComponent
              style={{ width: size, height: size, color: '#707070' }}
            />
          )}
        </Badge>
      </Tooltip>

      <input
        type="file"
        id="avatar"
        name="avatar"
        accept="image/png, image/jpeg"
        ref={imageInputRef}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const newImageFile = e.target.files ? e.target.files[0] : null;
          if (!newImageFile) return;

          if (['image/png', 'image/jpeg'].indexOf(newImageFile.type) === -1) {
            renderSnackbar([
              t('validator:please_upload_png_or_jpeg_under_2mb'),
            ]);
            if (imageInputRef.current) imageInputRef.current.value = '';
            return;
          }
          if (checkSize && newImageFile.size > MAX_SIZE_IMAGE) {
            renderSnackbar([
              t('validator:please_upload_png_or_jpeg_under_2mb'),
            ]);
            if (imageInputRef.current) imageInputRef.current.value = '';
            return;
          }

          const reader = new FileReader();
          reader.addEventListener('load', () => {
            const imageSrc = reader.result?.toString() || '';
            setDefaultImage(imageSrc);
          });
          reader.readAsDataURL(newImageFile);
        }}
        hidden
      />

      <Suspense fallback={<LoadingModal />}>
        {defaultImage && (
          <CropImagePopup
            imageUrl={defaultImage}
            onClose={() => {
              setDefaultImage(null);
              if (imageInputRef.current) imageInputRef.current.value = '';
            }}
            onUpload={onUpload}
          />
        )}
      </Suspense>
    </>
  );
};

export default UploadImage;
